import { DaysToExtendTrial, IAccount, ITrialExtensionSettings } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { IModalContext, ModalChildComponentContextKey } from '../../../models';
import { getDefaultDateStringValue } from '../../../models/UiUtils';
import { useToggle } from '../../../models/hooks/useToggle';
import { Button } from '../../../web/components/Button';
import { asModalComponent } from '../../../web/components/Modal';
import { TinyPopover } from '../../../web/components/TinyPopover';
import { InfoIcon } from '../../../web/components/svgs/icons/InfoIcon';
import { baseStyleSheet } from '../../../web/styles/styles';
import { TrialExtensionSelect } from './presentation';
import { styleSheet } from './styles';

interface IProps extends IModalContext<ITrialExtensionSettings> {
	account?: IAccount;
	className?: string;
	styles?: StyleDeclarationValue[];
}

const TrialExtensionSelectSFC: React.FC<IProps> = props => {
	const { account, className, parentModal, styles } = props;

	const diferenceInMs = moment().diff(moment(account?.planDetails?.trialExpirationDate));
	const trialIsExpired = moment.duration(diferenceInMs).asDays() >= 0;

	const [durationInDays, setExtensionDuration] = React.useState(7);
	const addDays = (date: Date, days: number) => {
		const copy = new Date(Number(date));
		copy.setDate(date.getDate() + days);
		return copy;
	};

	const currentExpirationDate = new Date(account?.planDetails?.trialExpirationDate);
	const currentDateOrExpirationDate = currentExpirationDate > new Date() ? currentExpirationDate : new Date();

	const [updatedTrialExpirationDate, setUpdatedTrialExpirationDate] = React.useState(
		addDays(currentDateOrExpirationDate, durationInDays)
	);

	React.useEffect(() => {
		if (account?.planDetails?.trialExtension) {
			setUpdatedTrialExpirationDate(addDays(currentDateOrExpirationDate, durationInDays));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onChangeExtensionDuration = (newExtension: DaysToExtendTrial) => {
		if (newExtension) {
			setExtensionDuration(newExtension);
			setUpdatedTrialExpirationDate(addDays(currentDateOrExpirationDate, newExtension));
		}
	};

	const onSaveClick = () => {
		const trialExtensionSettings = { durationInDays };
		parentModal?.onRequestClose(trialExtensionSettings, false);
	};

	const onRequestClose = () => {
		parentModal?.onRequestClose(null, true);
	};
	const [isOpen, , setOpen] = useToggle(false);

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} trial-extension-select ${className || ''}`}>
			<div className={css(styleSheet.title)}>
				Extend Trial Period
				<div onMouseEnter={setOpen(true)} onMouseLeave={setOpen(false)}>
					<TinyPopover
						anchor={<InfoIcon className={css(styleSheet.infoContainer)} />}
						isOpen={isOpen}
						dismissOnOutsideAction={true}
						onRequestClose={setOpen(false)}
						align='center'
						placement={['right']}
					>
						<div className={css(styleSheet.infoMessage, ...[])}>
							{trialIsExpired ? 'Trial expired on: ' : 'Current expiration date: '}

							{getDefaultDateStringValue(account.planDetails?.trialExpirationDate)}
						</div>
					</TinyPopover>
				</div>
			</div>
			<div className={css(baseStyleSheet.horizontalStack, styleSheet.expirationDetailContainer)}>
				<div>
					{'New trial expiration: '}
					<span className={css(baseStyleSheet.fontBold)}>{getDefaultDateStringValue(updatedTrialExpirationDate)}</span>
				</div>
			</div>
			<TrialExtensionSelect duration={durationInDays} setDuration={onChangeExtensionDuration} />
			<div className={css(styleSheet.rowContainer)}>
				<Button kind='primary' onClick={onSaveClick} disabled={false} label='Save' />
				<Button kind='reverse' onClick={onRequestClose} disabled={false} label='Cancel' />
			</div>
		</div>
	);
};
const TrialExtensionSelectAsObserver = observer(TrialExtensionSelectSFC);
const TrialExtensionSelectWithContext = inject(ModalChildComponentContextKey)(TrialExtensionSelectAsObserver);
export const TrialExtensionSelectModal = asModalComponent(TrialExtensionSelectWithContext, {
	className: 'trial-extension-select-modal',
	useDefaultHeader: true,
});
